import { MENU_NAMES } from '@/consts/menu-names.consts';
import { HandleType } from '@/enums/handle-type.enum';
import { I18N_KEYS } from '@/language/keys';
import type { Handle } from '@/types/node';
import type { MenuAction } from '@/components/Menu/Actions';

export const HANDLE_TOP_PX = 90;
export const HANDLE_SPACING_PX = 60;
export const HANDLE_TRANSITION_DELAY_MS = 50;

/**
 * How long a handle takes to slide to its new row on collapse or expand. Shared with the re-measure
 * loop in `useRowRemeasure` (`useHandleCollapse.ts`): React Flow caches handle geometry from a
 * synchronous read, so it has to keep re-reading for exactly as long as the handles are moving.
 */
export const COLLAPSE_TRANSITION_MS = 200;

/**
 * Extra time the re-measure loop runs past the transition's nominal end. Stopping exactly on it
 * leaves the last frame reading an eased handle a fraction short of its row, which shows up as an
 * edge landing off-centre on whichever input travelled furthest.
 */
export const COLLAPSE_REMEASURE_TAIL_MS = 100;

export type HandleRow =
  | { kind: 'group'; type: HandleType; memberKeys: string[]; rowIndex: number }
  | { kind: 'handle'; key: string; handle: Handle; rowIndex: number; isCollapsed: boolean };

/** One entry of a node's `handles.input` map, as `Object.entries` yields it. */
export type InputHandleEntry = [key: string, handle: Handle];

/** Puts a node's input handles in the order they render, which is what every reader here assumes. */
export const sortInputHandlesByOrder = (input: Record<string, Handle>): InputHandleEntry[] =>
  Object.entries(input).sort(([, left], [, right]) => (left.order || 0) - (right.order || 0));

/**
 * Relies on `Map` insertion order so the caller's `order` sort still governs vertical position.
 *
 * A collapsed member keeps a row of its own in the list but shares its group's `rowIndex`: it stays
 * mounted so React Flow keeps measuring it and its edge keeps rendering, and sitting on the group's
 * row is what makes that edge terminate at the group handle. A group only expands because the user
 * clicked it — `expandedTypes` is the sole input to that decision, so a collapsed member's
 * `pointerEvents: 'none'` (set by the renderer, not here) is never lifted mid-drag.
 */
export const resolveHandleRows = ({
  sortedInputHandles,
  resolveType,
  collapse,
}: {
  sortedInputHandles: InputHandleEntry[];
  resolveType: (key: string, fallback: HandleType | undefined) => HandleType;
  collapse: { expandedTypes: HandleType[] } | undefined;
}): HandleRow[] => {
  if (!collapse) {
    return sortedInputHandles.map(([key, handle], rowIndex) => ({
      kind: 'handle',
      key,
      handle,
      rowIndex,
      isCollapsed: false,
    }));
  }

  const membersByType = new Map<HandleType, InputHandleEntry[]>();
  for (const [key, handle] of sortedInputHandles) {
    const type = resolveType(key, handle?.type);
    const existingMembers = membersByType.get(type);
    if (existingMembers) existingMembers.push([key, handle]);
    else membersByType.set(type, [[key, handle]]);
  }

  const rows: HandleRow[] = [];
  let nextRowIndex = 0;

  for (const [type, members] of membersByType) {
    if (members.length === 1) {
      const [[soleKey, soleHandle]] = members;
      rows.push({ kind: 'handle', key: soleKey, handle: soleHandle, rowIndex: nextRowIndex++, isCollapsed: false });
      continue;
    }

    const groupRowIndex = nextRowIndex++;
    rows.push({ kind: 'group', type, memberKeys: members.map(([memberKey]) => memberKey), rowIndex: groupRowIndex });

    const expanded = collapse.expandedTypes.includes(type);
    for (const [memberKey, memberHandle] of members) {
      rows.push({
        kind: 'handle',
        key: memberKey,
        handle: memberHandle,
        rowIndex: expanded ? nextRowIndex++ : groupRowIndex,
        isCollapsed: !expanded,
      });
    }
  }

  return rows;
};

/**
 * A row's on-screen position, as a string two renders can compare for equality. `rowIndex` alone
 * discriminates every toggle: a group's members always move off (or onto) its shared index when
 * expanding (or collapsing) it, since a group always has at least two members, and that reassignment
 * cascades into every row below. Two renders producing the same key have every row in the same
 * place, so nothing needs to be re-measured.
 */
export const buildRowLayoutKey = (rows: HandleRow[]): string =>
  rows
    .map((row) => (row.kind === 'group' ? `g:${row.type}:${row.rowIndex}` : `h:${row.key}:${row.rowIndex}`))
    .join(',');

const HANDLE_TYPE_I18N_KEY: Record<HandleType, string> = {
  [HandleType.Image]: I18N_KEYS.GENERAL.HANDLE_TYPES.IMAGE,
  [HandleType.Video]: I18N_KEYS.GENERAL.HANDLE_TYPES.VIDEO,
  [HandleType.Audio]: I18N_KEYS.GENERAL.HANDLE_TYPES.AUDIO,
  [HandleType.ThreeDee]: I18N_KEYS.GENERAL.HANDLE_TYPES.THREE_D,
  [HandleType.Text]: I18N_KEYS.GENERAL.HANDLE_TYPES.TEXT,
  [HandleType.Seed]: I18N_KEYS.GENERAL.HANDLE_TYPES.SEED,
  [HandleType.Mask]: I18N_KEYS.GENERAL.HANDLE_TYPES.MASK,
  [HandleType.Lora]: I18N_KEYS.GENERAL.HANDLE_TYPES.LORA,
  [HandleType.Number]: I18N_KEYS.GENERAL.HANDLE_TYPES.NUMBER,
  [HandleType.Boolean]: I18N_KEYS.GENERAL.HANDLE_TYPES.BOOLEAN,
  [HandleType.Array]: I18N_KEYS.GENERAL.HANDLE_TYPES.ARRAY,
  [HandleType.KlingElement]: I18N_KEYS.GENERAL.HANDLE_TYPES.KLING_ELEMENT,
  [HandleType.Keyframe]: I18N_KEYS.GENERAL.HANDLE_TYPES.KEYFRAME,
  [HandleType.Any]: I18N_KEYS.GENERAL.HANDLE_TYPES.ANY,
};

/** `_lower` because the label reads as running text ("7 texts"); casing is the translation's job. */
export const handleTypeLabelKey = (type: HandleType): string => `${HANDLE_TYPE_I18N_KEY[type]}_lower`;

export const collapseStats = ({
  sortedInputHandles,
  resolveType,
}: {
  sortedInputHandles: InputHandleEntry[];
  resolveType: (key: string, fallback: HandleType | undefined) => HandleType;
}): { inputCount: number; groupCount: number; collapsibleInputCount: number } => {
  const groupRows = resolveHandleRows({
    sortedInputHandles,
    resolveType,
    collapse: { expandedTypes: [] },
  }).filter((row): row is Extract<HandleRow, { kind: 'group' }> => row.kind === 'group');

  return {
    inputCount: sortedInputHandles.length,
    groupCount: groupRows.length,
    collapsibleInputCount: groupRows.reduce((total, group) => total + group.memberKeys.length, 0),
  };
};

/**
 * `renderGroupHeader` draws its own rule above a group that is not first, and
 * `useNodeHistoryMenuItem` is divider-fenced on both sides — so appending a group straight after it
 * puts two rules back to back. Callers drop the trailing one rather than changing the menu
 * primitive that every other menu in the app shares.
 *
 * The divider shape is matched inline rather than via `isDividerMenuItem`: that predicate lives in
 * `Actions.tsx`, and importing it as a value would pull a `sessionStorage`-touching module into this
 * file's runtime graph, which must stay free of DOM dependencies.
 */
export const endsWithDivider = (items: MenuAction[]): boolean => {
  const last = items[items.length - 1];
  return last !== undefined && 'type' in last && last.type === 'divider';
};

/** An action row, not a `checkbox_group`: only this variant supports `disabledTooltip`. */
export const buildCollapseHandlesMenuItem = ({
  isFlagEnabled,
  isCollapsed,
  inputCount,
  canAddInputs = false,
  onToggle,
  t,
}: {
  isFlagEnabled: boolean;
  isCollapsed: boolean;
  inputCount: number;
  canAddInputs?: boolean;
  onToggle: () => void;
  t: (key: string) => string;
}): MenuAction | undefined => {
  if (!isFlagEnabled || (inputCount < 2 && !canAddInputs)) return undefined;

  return {
    type: 'group',
    title: t(I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU.HANDLES),
    items: [
      {
        biEventKey: { menu: MENU_NAMES.HANDLE_COLLAPSE, action: 'collapse_handles_by_type' },
        name: t(I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU.COLLAPSE_HANDLES_BY_TYPE),
        selected: isCollapsed,
        action: onToggle,
        disabled: inputCount < 2,
        collapsePresentation: true,
        disabledTooltip: t(I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU.COLLAPSE_HANDLES_NO_INPUTS),
      },
    ],
  };
};

/** Read the rendered collapsed membership, including Any groups held stable while selected. */
export function isCollapsedEdgeUpdateBlocked({
  enabled,
  eventTarget,
  flowElement,
  edges,
  explicitSelectedEdges,
}: {
  enabled: boolean;
  eventTarget: Element | null;
  flowElement: Element;
  edges: readonly { id: string; targetHandle?: string | null }[];
  explicitSelectedEdges: readonly string[];
}): boolean {
  if (!enabled || !eventTarget?.closest('.react-flow__edgeupdater-target')) return false;
  const edgeElement = eventTarget.closest('.react-flow__edge');
  const edge = edges.find((edge) => edgeElement?.getAttribute('data-testid') === `rf__edge-${edge.id}`);
  if (!edge?.targetHandle || explicitSelectedEdges.includes(edge.id)) return false;
  return Array.from(flowElement.querySelectorAll('.react-flow__handle.target:not(.connectable)')).some(
    (handle) => handle.getAttribute('data-handleid') === edge.targetHandle,
  );
}

/** Explicitly selected edges must receive the pointer before overlapping incident highlights. */
export function prioritizeExplicitEdges<T extends { id: string; zIndex?: number }>(
  edges: T[],
  enabled: boolean,
  explicitSelectedEdges: readonly string[],
): T[] {
  if (!enabled || explicitSelectedEdges.length === 0) return edges;
  const selected = new Set(explicitSelectedEdges);
  const zIndex = edges.reduce((max, edge) => Math.max(max, edge.zIndex ?? 0), 0) + 1;
  return edges.map((edge) => (selected.has(edge.id) ? { ...edge, zIndex } : edge));
}
